import { Component, Input, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutoNumberingService } from './services/auto-numbering.service';
import { NumberingConfig } from './models/numbering-config.interface';
import {
  NumberingElement,
  DropdownElementConfig,
  SequenceElementConfig,
  YearElementConfig,
  MonthElementConfig,
  TextElementConfig
} from './models/numbering-element.interface';
import { TranslatePipe } from '../../../pipes/translate.pipe';

@Component({
  selector: 'app-form-auto-numbering',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './form-auto-numbering.component.html',
  styleUrls: ['./form-auto-numbering.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutoNumberingComponent),
      multi: true
    }
  ]
})
export class FormAutoNumberingComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() config: NumberingConfig = { elements: [] };

  value: string = '';
  displayValue: string = '';
  dropdownValues: { [key: string]: string } = {};
  dropdownElements: NumberingElement[] = [];
  errorMessage: string = '';
  generatingNumber: boolean = false;

  private destroy$ = new Subject<void>();
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(private autoNumberingService: AutoNumberingService) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    // 驗證配置
    const validation = this.autoNumberingService.validateConfig(this.config);
    if (!validation.isValid) {
      this.errorMessage = validation.error || '配置錯誤';
      return;
    }

    // 篩選出下拉選單元素
    this.dropdownElements = this.config.elements.filter(element => element.type === 'dropdown');

    // 如果沒有下拉選單，直接生成編號
    if (this.dropdownElements.length === 0) {
      this.generateNumber();
    }
  }

  private async generateNumber(): Promise<void> {
    if (!this.canGenerate) {
      return;
    }

    this.generatingNumber = true;
    this.errorMessage = '';

    try {
      const result = await this.autoNumberingService.generateNumber(this.config, this.dropdownValues);

      if (result.success) {
        this.value = result.number;
        this.displayValue = result.number;
        this.onChange(this.value);
      } else {
        this.errorMessage = result.error || '編號生成失敗';
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : '編號生成失敗';
    } finally {
      this.generatingNumber = false;
    }
  }

  regenerateNumber(): void {
    this.generateNumber();
  }

  get canGenerate(): boolean {
    // 檢查所有下拉選單是否都已選擇
    for (const element of this.dropdownElements) {
      const key = `dropdown_${element.order}`;
      if (!this.dropdownValues[key]) {
        return false;
      }
    }
    return true;
  }

  onDropdownChange(): void {
    if (this.canGenerate) {
      this.generateNumber();
    }
  }

  // 取得下拉選單標籤
  getDropdownLabel(element: NumberingElement, index: number): string {
    const config = element.config as DropdownElementConfig;
    return config.label || `選項 ${index + 1}`;
  }

  // 取得下拉選單佔位符
  getDropdownPlaceholder(element: NumberingElement): string {
    const config = element.config as DropdownElementConfig;
    return config.placeholder || '請選擇';
  }

  // 取得下拉選單選項
  getDropdownOptions(element: NumberingElement): Array<{label: string, value: string}> {
    const config = element.config as DropdownElementConfig;
    return config.options || [];
  }

  // ControlValueAccessor 實作
  writeValue(value: string): void {
    this.value = value || '';
    this.displayValue = this.value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // 處理禁用狀態
  }
}
